import { Fragment, useState } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import VisibilityToggle from "src/Components/Shared/VisibilityToggle/VisibilityToggle";
import EndPoints from "src/Constants/EndPoints";
import useLoading from "src/Hooks/Custom/useLoading";
import useApiFetch from "src/Hooks/Shared/useApiFetch";
import useApiFetchInfinite from "src/Hooks/Shared/useApiFetchInfinite";
import useApiMutation from "src/Hooks/Shared/useApiMutation";
import AppStateHandler from "src/StateHandlers/AppStateHandler";
import ConfirmModal from "../ConfirmModal/ConfirmModal";
import GroupModal from "../GroupModal/GroupModal";
import PostList from "../PostList/PostList";
import PostModal from "../PostModal/PostModal";
import { useTranslation } from "react-i18next";

function GroupDetail(props: any) {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const { path } = useRouteMatch();
  const { user } = AppStateHandler.getState();
  const [confirmModal, setConfirmModal] = useState<boolean>(false);
  const [confirmAction, setConfirmAction] = useState<Function>();
  const [actionStatus, setActionStatus] = useState<any>('');
  const { groupData, setGroupData, groupId = groupData.id, postId } = props;
  const { data: detailResponse, refetch: fetchGroup } = useApiFetch(
    [
      EndPoints.groupDetail +
      "?groupId=" +
      groupId +
      "&subId=" +
      user?.username,
    ],
    {}
  );
  const groupDetail = detailResponse?.data ?? groupData;
  const {
    id,
    group_icon,
    title,
    total_members,
    description,
    subId: groupOwnerId,
    is_private,
    is_requested,
    is_member,
    is_admin
  } = groupDetail;
  const showGroupDetail = path === "/groups/:id";
  const [postData, setPostData] = useState<any>([]);
  const [memberId, setMemberId] = useState<any>(0);
  const [modalVisibility, setModalVisibility] = useState<boolean>(false);
  const [groupModalVisibility, setGroupModalVisibility] = useState<boolean>(
    false
  );

  const { data: adminsResponse, refetch: refetchAdmins } = useApiFetch(
    [
      `${EndPoints.groupMembers}?subId=${user?.username}&groupId=${groupId}&type=admins`,
    ],
    {}
  );
  const { data: invitesResponse, refetch: refetchInvites } = useApiFetch(
    [
      `${EndPoints.groupMembers}?subId=${user?.username}&groupId=${groupId}&type=requested`,
    ],
    {}
  );

  const { Items: admins = [] } = adminsResponse?.data ?? {};
  const { Items: invites = [] } = invitesResponse?.data ?? {};

  const { data, refetch, status } = useApiFetchInfinite(
    [`${EndPoints.groupMembers}?subId=${user?.username}&groupId=${groupId}`],
    {
      getFetchMore: (lastData) => {
        const nextPage = lastData?.data?.Page;
        const totalPages = lastData?.data?.TotalPages;

        return parseInt(nextPage ?? "1", 10) < totalPages
          ? `&page=${parseInt(nextPage ?? "1", 10) + 1}`
          : null;
      },
    }
  );
  useLoading(status);
  const membersData = (Array.isArray(data) ? data : [])
    .map((_) => _.data.Items)
    .reduce((a, b) => a.concat(b), []);

  const [deleteGroup, { status: deleteStatus }] = useApiMutation({
    onSuccess: () => {
      setConfirmModal(false);
      history.goBack();
    },
  });

  const [leaveGroup, { status: leaveStatus }] = useApiMutation({
    onSuccess: () => {
      setConfirmModal(false);
      history.goBack();
    },
  });

  const [acceptMember, { status: acceptStatus }] = useApiMutation({
    onSuccess: () => {
      refetchInvites();
      refetch();
      setConfirmModal(false);
    },
  });

  const [rejectMember, { status: rejectStatus }] = useApiMutation({
    onSuccess: () => {
      refetchInvites();
      setConfirmModal(false);
    },
  });

  const [removeMember, { status: removeStatus }] = useApiMutation({
    onSuccess: () => {
      refetch();
      refetchInvites();
      setConfirmModal(false);
    },
  });

  const [removeAdmin, { status: adminStatus }] = useApiMutation({
    onSuccess: () => {
      refetchAdmins();
      refetch();
      setConfirmModal(false);
    },
  });

  const [makeAdmin, { status: makeStatus }] = useApiMutation({
    onSuccess: () => {
      refetchAdmins();
      refetch();
      setConfirmModal(false);
    },
  });

  const [joinGroup, { status: joinStatus }] = useApiMutation({
    onSuccess: () => {
      fetchGroup();
    },
  });

  const editGroupHandler = (e: any) => {
    e.preventDefault();
    setGroupData(groupDetail);
    setGroupModalVisibility(true);
  };

  const deleteGroupHandler = () => {
    if (deleteStatus === "loading" || actionStatus === "cancel") {
      return;
    }
    deleteGroup({
      url: EndPoints.deleteGroup,
      method: "DELETE",
      data: {
        subId: user?.username,
        groupId: groupId,
      },
    });
    if (deleteStatus === "success") {
      setGroupData([]);
    }
  };

  const leaveGroupHandler = () => {
    if (leaveStatus === "loading" || actionStatus === "cancel") {
      return;
    }
    leaveGroup({
      url: EndPoints.leaveGroup,
      data: {
        subId: user?.username,
        groupId: groupId,
      },
    });
    if (leaveStatus === "success") {
      setGroupData([]);
    }
  };

  const acceptMemberHandler = (memberId: any) => {
    if (acceptStatus === "loading" || actionStatus === "cancel" || memberId === 0) {
      return;
    }
    acceptMember({
      url: EndPoints.addMember,
      data: {
        subId: user?.username,
        memberSubId: memberId,
        groupId: groupId,
      },
    });
  };

  const rejectMemberHandler = (memberId: any) => {
    if (rejectStatus === "loading" || actionStatus === "cancel" || memberId === 0) {
      return;
    }
    rejectMember({
      url: EndPoints.rejectMember,
      method: "PUT",
      data: {
        subId: user?.username,
        memberId: memberId,
      },
    });
  };

  const removeMemberHandler = (memberId: any) => {
    if (removeStatus === "loading" || actionStatus === "cancel" || memberId === 0) {
      return;
    }
    removeMember({
      url: EndPoints.removeMember,
      data: {
        subId: user?.username,
        memberId: memberId,
      },
    });
  };

  const removeAdminHandler = (memberId: any) => {
    if (adminStatus === "loading" || actionStatus === "cancel" || memberId === 0) {
      return;
    }
    removeAdmin({
      url: EndPoints.removeAdmin,
      data: {
        subId: user?.username,
        memberId: memberId,
      },
    });
  };

  const makeAdminHandler = (memberId: any) => {
    console.log('testing')
    if (makeStatus === "loading" || actionStatus === "cancel" || memberId === 0) {
      return;
    }
    makeAdmin({
      url: EndPoints.makeAdmin,
      data: {
        subId: user?.username,
        memberId: memberId,
      },
    });
  };

  const joinHandler = (e: any) => {
    e.preventDefault();
    console.log(joinStatus, actionStatus);
    if (joinStatus === "loading" || actionStatus === "cancel") {
      return;
    }
    joinGroup({
      url: EndPoints.joinGroup,
      data: {
        subId: user?.username,
        groupId: id,
      },
    });
  };

  return (
    <>
      <div className={showGroupDetail ? "col-md-12" : "col-md-9"}>
        <div className="member_pages_section bg-transparent shadow-none">
          <div className="row">
            <div className={showGroupDetail ? "col-lg-9" : "col-md-12"}>
              <div className="col_selector">
                {showGroupDetail ? (
                  <>
                    <div className="home_search_section position-relative shadow-none mb-4 post_feed p-0 group_detail_top">
                      <img
                        src={group_icon}
                        className="img-fluid w-100 object_fit"
                        alt=""
                        style={{ height: 250 }}
                      />
                      {/* <Link to="/groups"> */}
                      <span className="member_back_btn cursor-pointer" onClick={() => history.goBack()}>
                        <i
                          className="fa fa-chevron-left text-white"
                          style={{
                            position: "relative",
                            left: "12px",
                            top: "11px",
                          }}
                          aria-hidden="true"
                        ></i>
                      </span>
                      {/* </Link> */}
                      <div className="member-banner">
                        <div className={`d-flex justify-content-between align-items-center position-relative member-banner-details ${i18n.dir() === "rtl" ? "text-right":""} `}>
                          <h4 className="fz16 mb-1 mb-0 poppinssemibold text-white">
                            {title} <br />
                            <span className="fz10 poppinsregular text-light">
                              {description}
                            </span>
                          </h4>
                          {(is_private && is_requested && !is_member) ? (
                            <button
                              className={`btn custom_btn fz14  ${i18n.dir() === "rtl" ? "Almarbold" : "poppinsbold"}`}
                            >
                              {t("Requested")}
                            </button>
                          ) : <></>}
                          {(is_private && !is_requested && !is_admin) ? (
                            <button
                            className={`btn custom_btn fz14  ${i18n.dir() === "rtl" ? "Almarbold" : "poppinsbold"}`}
                            onClick={joinHandler}
                            >
                              <VisibilityToggle
                                visible={joinStatus === "loading"}
                              >
                                <span className="spinner-border spinner-border-sm"></span>
                              </VisibilityToggle>
                              {t("Join")}
                            </button>
                          ) : (
                              <></>
                            )}
                          {(is_member || is_admin)? (
                            <button
                            className={`btn custom_btn fz14  ${i18n.dir() === "rtl" ? "Almarbold" : "poppinsbold"}`}
                              onClick={() => setModalVisibility(true)}
                            >
                              {t("Post")}
                            </button>
                          ) : (
                              <></>
                            )}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                    <></>
                  )}
                {(is_member ||is_admin) ? (
                  <PostList
                    endPoint={EndPoints.getGroupPosts}
                    placeholderMessage={"No posts in this group"}
                    postData={postData}
                    setPostData={setPostData}
                    postId={postId}
                    groupId={groupId}
                    setModalVisibility={setModalVisibility}
                  />
                ) : (
                    <></>
                  )}
              </div>
            </div>
            {showGroupDetail ? (
              <>
                <div className="col-lg-3">
                  <div
                    className="col_selector bg-white px-3 pb-2 group_right"
                    style={{ borderRadius: "9px" }}
                  >
                    <div className="messages members pt-4 d-flex justify-content-between pr-0">
                      <VisibilityToggle visible={is_member || is_admin}>
                        <div className="d-flex justify-content-between mb-4 align-items-center">
                          <h3 className="fz16 mb-0">{total_members} {t("Members")}</h3>
                        </div>
                        <div className={`slider_down_arrow position-relative ${i18n.dir() === 'rtl'?'pr':'pl'}-4`}>
                          <i
                            className="fa fa-angle-down fz26"
                            aria-hidden="true"
                          ></i>
                          <div className={`user_actions ${i18n.dir()==='rtl'?'user_actions_posRTL':'user_actions_posLTR'}`}>
                            <ul className={`list-unstyled  ${i18n.dir()==='rtl'?'text-right Almaraibold':'text-left poppinsbold'} mb-0 `}>
                              <VisibilityToggle
                                visible={user.username === groupOwnerId}
                              >
                                <li>
                                  <a
                                    className="py-4"
                                    href="/"
                                    onClick={editGroupHandler}
                                  >
                                    <i
                                      className={`fa fa-pencil fz18 ${i18n.dir()=== 'rtl'? "ml-4 " : "mr-4"}`}
                                      aria-hidden="true"
                                    ></i>
                                    {t("Edit")}
                                  </a>
                                </li>
                                <li>
                                  <a
                                    className="py-4"
                                    href="/"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setConfirmModal(true);
                                      setActionStatus(() => deleteStatus);
                                      setConfirmAction(() => deleteGroupHandler);
                                    }}
                                  >
                                    <i
                                      className={`fa fa-trash  fz18 ${i18n.dir()=== 'rtl'? "ml-4 " : "mr-4"}`}
                                      aria-hidden="true"
                                    ></i>
                                    <VisibilityToggle
                                      visible={deleteStatus === "loading"}
                                    >
                                      <span className="spinner-border spinner-border-sm"></span>
                                    </VisibilityToggle>
                                    {t("Delete")}
                                  </a>
                                </li>
                              </VisibilityToggle>
                              <VisibilityToggle
                                visible={user.username !== groupOwnerId}
                              >
                                <li>
                                  <a
                                    className="py-4 text-danger"
                                    href="/"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setConfirmModal(true);
                                      setActionStatus(() => leaveStatus);
                                      setConfirmAction(() => leaveGroupHandler);
                                    }}
                                  >
                                    <i
                                      className={`fa fa-sign-out fz18 ${i18n.dir()=== 'rtl'? "ml-4 " : "mr-4"}`}
                                      aria-hidden="true"
                                    ></i>
                                    <VisibilityToggle
                                      visible={leaveStatus === "loading"}
                                    >
                                      <span className="spinner-border spinner-border-sm"></span>
                                    </VisibilityToggle>
                                    {t("Leave Group")}
                                  </a>
                                </li>
                              </VisibilityToggle>
                            </ul>
                          </div>
                        </div>
                      </VisibilityToggle>
                    </div>
                    <div className={`${i18n.dir() ==='rtl'?'text-right':''}`} style={{ color: "gray", marginBottom: "10px" }}>
                      {admins.length > 1 ? t("Admins") : t("Admin")}
                    </div>
                    {admins.map((a: any, i: number) => {
                      return (
                        <div key={i}>
                          <div className={`d-flex justify-content-between position-relative ${i18n.dir()==='rtl'?'pr-5':'pl-5'}`}>
                            <div className={`message_user_icon ${i18n.dir()==='rtl'?'message_user_icon_posRTL':'message_user_icon_posLTR'}`}>
                              <img
                                src={
                                  a.user_image !== ""
                                    ? a.user_image
                                    : "/assets/images/profile-camera.png"
                                }
                                className="rounded-circle object_fit"
                                width="40"
                                height="40"
                                alt=""
                              />
                            </div>
                            <h4 className= {`fz16 mb-1 ${i18n.dir()==='rtl'?'pr-3':'pl-3'} mb-0 poppinssemibold min_height text-break`}>
                              {a.user_name} <br />
                              <span className="fz10 poppinsregular text-secondary"></span>
                            </h4>
                            <VisibilityToggle
                              visible={
                                user.username === groupOwnerId &&
                                a.subId !== groupOwnerId
                              }
                            >
                              <div className="slider_down_arrow position-relative">
                                <i
                                  className="fa fa-angle-down fz26 mr-4"
                                  aria-hidden="true"
                                ></i>
                                <div className={`user_actions ${i18n.dir()==='rtl'?'user_actions_posRTL':'user_actions_posLTR'}`}>
                                  <ul className={`list-unstyled  ${i18n.dir()==='rtl'?'text-right':'text-left'} mb-0 poppinsbold`}>
                                    <li>
                                      <a
                                        className="py-4 text-danger"
                                        href="/"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          setMemberId((prevId: any) => a.id)
                                          setConfirmModal(true);
                                          setActionStatus(() => removeStatus);
                                          setConfirmAction(() => removeAdminHandler);
                                        }}
                                      >
                                        <i
                                          className="fa fa-user-times mr-4 fz18"
                                          aria-hidden="true"
                                        ></i>
                                        <VisibilityToggle
                                          visible={adminStatus === "loading"}
                                        >
                                          <span className="spinner-border spinner-border-sm"></span>
                                        </VisibilityToggle>
                                        {t("Remove")}
                                      </a>
                                    </li>
                                  </ul>
                                </div>
                              </div>
                            </VisibilityToggle>
                          </div>
                          <hr />
                        </div>
                      );
                    })}
                    <VisibilityToggle
                      visible={invites.length !== 0 && is_member}
                    >
                      <>
                        <div className={i18n.dir() === "rtl" ? "text-right":""} style={{ color: "gray", marginBottom: "10px" }}>
                          {t("Invites")}
                        </div>
                        {invites.map((_i: any, i: number) => {
                          return (
                            <div key={i} className="">
                              <div className={`d-flex justify-content-between position-relative ${i18n.dir()==='rtl'?'pr-5':'pl-5'}`}>
                                <div className={`message_user_icon ${i18n.dir()==='rtl'?'message_user_icon_posRTL':'message_user_icon_posLTR'}`}>
                                  <img
                                    src={
                                      _i.user_image !== ""
                                        ? _i.user_image
                                        : "/assets/images/profile-camera.png"
                                    }
                                    className="rounded-circle object_fit"
                                    width="40"
                                    height="40"
                                    alt=""
                                  />
                                </div>
                                <h4 className={`fz16 mb-1 ${i18n.dir()==='rtl'?'pr-3':'pl-3'} mb-0 poppinssemibold text-break`}>
                                  {_i.user_name.length > 25
                                    ? _i.user_name.substr(0, 20) + "..."
                                    : _i.user_name}{" "}
                                  <br />
                                  <span className="fz10 poppinsregular text-secondary"></span>
                                </h4>
                                <VisibilityToggle
                                  visible={user.username === groupOwnerId}
                                >
                                  <div className="slider_down_arrow position-relative">
                                    <i
                                      className="fa fa-angle-down fz26 mr-4"
                                      aria-hidden="true"
                                    ></i>
                                    <div className={`user_actions ${i18n.dir()==='rtl'?'user_actions_posRTL':'user_actions_posLTR'}`}>
                                      <ul className={`list-unstyled  ${i18n.dir()==='rtl'?'text-right':'text-left'} mb-0 poppinsbold`}>
                                        <VisibilityToggle
                                          visible={
                                            _i.is_requested && !_i.is_member
                                          }
                                        >
                                          <li>
                                            <a
                                              className="py-4"
                                              href="/"
                                              onClick={(e) => {
                                                e.preventDefault();
                                                setConfirmModal(true);
                                                setActionStatus(() => acceptStatus);
                                                setMemberId((prevId: any) => _i.subId)
                                                setConfirmAction(() => acceptMemberHandler);
                                              }}
                                            >
                                              <i
                                                className="fa fa-thumbs-up mr-4 fz18"
                                                aria-hidden="true"
                                              ></i>
                                              <VisibilityToggle
                                                visible={
                                                  acceptStatus === "loading"
                                                }
                                              >
                                                <span className="spinner-border spinner-border-sm"></span>
                                              </VisibilityToggle>
                                              {t("Accept")}
                                            </a>
                                          </li>
                                        </VisibilityToggle>
                                        <VisibilityToggle
                                          visible={
                                            _i.is_requested && !_i.is_member
                                          }
                                        >
                                          <li>
                                            <a
                                              className="py-4"
                                              href="/"
                                              onClick={(e) => {
                                                e.preventDefault();
                                                setConfirmModal(true);
                                                setActionStatus(() => rejectStatus);
                                                setMemberId((prevId: any) => _i.id)
                                                setConfirmAction(() => rejectMemberHandler);
                                              }}
                                            >
                                              <i
                                                className="fa fa-thumbs-down mr-4 fz18"
                                                aria-hidden="true"
                                              ></i>
                                              <VisibilityToggle
                                                visible={
                                                  rejectStatus === "loading"
                                                }
                                              >
                                                <span className="spinner-border spinner-border-sm"></span>
                                              </VisibilityToggle>
                                              {t("Reject")}
                                            </a>
                                          </li>
                                        </VisibilityToggle>
                                      </ul>
                                    </div>
                                  </div>
                                </VisibilityToggle>
                              </div>
                              <hr />
                            </div>
                          );
                        })}
                      </>
                    </VisibilityToggle>
                    <VisibilityToggle visible={membersData.length !== 0}>
                      <>
                        <div className={i18n.dir() === "rtl" ? "text-right":""} style={{ color: "gray", marginBottom: "10px" }}>
                          {t("Members")}
                        </div>
                        {membersData.map((m: any, i: number) => {
                          return (
                            <div key={i} className="">
                              <div className={`d-flex justify-content-between position-relative ${i18n.dir()==='rtl'?'pr-5':'pl-5'}`}>
                                <div className={`message_user_icon ${i18n.dir()==='rtl'?'message_user_icon_posRTL':'message_user_icon_posLTR'}`}>
                                  <img
                                    src={
                                      m.user_image !== ""
                                        ? m.user_image
                                        : "/assets/images/profile-camera.png"
                                    }
                                    className="rounded-circle object_fit"
                                    width="40"
                                    height="40"
                                    alt=""
                                  />
                                </div>
                                <h4
                                  title={
                                    m.user_name.length > 25 ? m.user_name : ""
                                  }
                                  className={`fz16 mb-1 ${i18n.dir()==='rtl'?'pr-3':'pl-3'} mb-0 poppinssemibold min_height text-break`}
                                >
                                  {m.user_name.length > 25
                                    ? m.user_name.substr(0, 20) + "..."
                                    : m.user_name}{" "}
                                  <br />
                                  <span className="fz10 poppinsregular text-secondary"></span>
                                </h4>
                                <VisibilityToggle
                                  visible={user.username === groupOwnerId}
                                >
                                  <div className={`slider_down_arrow position-relative ${i18n.dir() === 'rtl'?'pr':'pl'}-4`}>
                                    <i
                                      className="fa fa-angle-down fz26 py-2"
                                      aria-hidden="true"
                                    ></i>
                                    <div className={`user_actions ${i18n.dir()==='rtl'?'user_actions_posRTL':'user_actions_posLTR'}`}>
                                      <ul className={`list-unstyled  ${i18n.dir()==='rtl'?'text-right':'text-left'} mb-0 poppinsbold`}>
                                        <VisibilityToggle visible={m.is_member}>
                                          <li>
                                            <a
                                              className="py-4"
                                              href="/"
                                              onClick={(e) => {
                                                e.preventDefault();
                                                setConfirmModal(true);
                                                setActionStatus(() => makeStatus);
                                                setMemberId((prevId: any) => m.id)
                                                setConfirmAction(() => makeAdminHandler);
                                              }}
                                            >
                                              <i
                                                className="fa fa-user-secret mr-4 fz18"
                                                aria-hidden="true"
                                              ></i>
                                              <VisibilityToggle
                                                visible={
                                                  removeStatus === "loading"
                                                }
                                              >
                                                <span className="spinner-border spinner-border-sm"></span>
                                              </VisibilityToggle>
                                              {t("Make Admin")}
                                            </a>
                                          </li>
                                          <li>
                                            <a
                                              className="py-4 text-danger"
                                              href="/"
                                              onClick={(e) => {
                                                e.preventDefault();
                                                setConfirmModal(true);
                                                setActionStatus(() => makeStatus);
                                                setMemberId((prevId: any) => m.id)
                                                setConfirmAction(() => removeMemberHandler);
                                              }}
                                            >
                                              <i
                                                className="fa fa-user-times mr-4 fz18"
                                                aria-hidden="true"
                                              ></i>
                                              <VisibilityToggle
                                                visible={
                                                  removeStatus === "loading"
                                                }
                                              >
                                                <span className="spinner-border spinner-border-sm"></span>
                                              </VisibilityToggle>
                                              {t("Remove")}
                                            </a>
                                          </li>
                                        </VisibilityToggle>
                                      </ul>
                                    </div>
                                  </div>
                                </VisibilityToggle>
                              </div>
                              <hr />
                            </div>
                          );
                        })}
                      </>
                    </VisibilityToggle>
                  </div>
                </div>
              </>
            ) : (
                <></>
              )
            }
          </div >
        </div >
      </div >
      {
        groupModalVisibility ? (
          <GroupModal
            refetch={fetchGroup}
            setGroupData={setGroupData}
            groupData={groupData}
            showModal={groupModalVisibility}
            setModalVisibility={setGroupModalVisibility}
          />
        ) : (
            <Fragment />
          )
      }
      {
        modalVisibility ? (
          <PostModal
            postData={postData}
            setPostData={setPostData}
            groupId={id}
            showModal={modalVisibility}
            setModalVisibility={setModalVisibility}
          />
        ) : (
            <Fragment />
          )
      }
      {
        confirmModal ? (
          <ConfirmModal
            showModal={confirmModal}
            setModalVisibility={setConfirmModal}
            confirmAction={confirmAction}
            ConfirmationText={"Are you sure?"}
            CancelBtnText={"Cancel"}
            ConfirmBtnText={"Yes"}
            memberId={memberId}
            status={actionStatus}
            setActionStatus={setActionStatus}
          />
        ) : (
            <></>
          )
      }
    </>
  );
}
export default GroupDetail;
